<script lang="ts">
	/**
	 * Language switcher.
	 *
	 * Navigates to the current page's counterpart in another locale, resolved by
	 * `alternatePathFor`. It used to call Paraglide's `setLocale()`, which rebuilds
	 * the URL by prefixing `/th` — the assumption AGENTS.md warns about hardest,
	 * because WPML translates page, category, post and some product slugs. That
	 * 404'd on 31 of the 60 English pages, and on the way back from every
	 * Thai-slugged URL.
	 *
	 * Rendered as real `<a href>` elements, so the destination is visible on hover,
	 * middle-click works, and SvelteKit's client router handles it — `setLocale`
	 * assigned `window.location.href`, a full document reload.
	 *
	 * **Unavailable locales are listed, not hidden.** A row that silently vanishes
	 * reads as a bug; a row that says why does not. Every locale has all six
	 * registry pages and the terms, so those pages show no gaps. Two things still
	 * produce genuine ones, and neither is a bug here:
	 *
	 * - **The privacy policy is English and Thai only**, so six rows are unavailable
	 *   on that page.
	 * - **Articles are published per file, not per folder.** `ja` holds 3 at
	 *   `draft: true` and `fr`, `es`, `ru` and `zh` hold 10 each, so an article can
	 *   be live in `en`, `th` and `vi` and unavailable in five other locales. That
	 *   is what the reader sees on `/learn/dispensary-to-medical-cannabis-clinic-thailand`.
	 */
	import type { SiteLocale } from '$lib/services/products';
	import { getLocale, locales } from '$lib/paraglide/runtime';
	import { page } from '$app/state';
	import { alternatePathFor } from '$lib/i18n/alternate-path';
	import * as m from '$lib/paraglide/messages';
	import flagTH from './flag_th.svg';
	import flagUK from './flag_uk.svg';
	import flagJP from './flag_jp.svg';
	import flagFR from './flag_fr.svg';
	import flagES from './flag_es.svg';
	import flagRU from './flag_ru.svg';
	import flagCN from './flag_cn.svg';
	import flagVN from './flag_vn.svg';

	let isOpen = $state(false);
	const toggleOpen = () => (isOpen = !isOpen);
	const handleCancel = () => (isOpen = false);

	/**
	 * Each language named in its own language, which is the convention for a
	 * switcher: someone looking for Japanese is scanning for 日本語, not for the
	 * word "Japanese" written in a language they may not read.
	 */
	const LANGUAGES: Record<string, { name: string; flag: string; code: string }> = {
		en: { name: 'English', flag: flagUK, code: 'EN' },
		th: { name: 'ไทย', flag: flagTH, code: 'TH' },
		ja: { name: '日本語', flag: flagJP, code: 'JA' },
		fr: { name: 'Français', flag: flagFR, code: 'FR' },
		es: { name: 'Español', flag: flagES, code: 'ES' },
		ru: { name: 'Русский', flag: flagRU, code: 'RU' },
		zh: { name: '简体中文', flag: flagCN, code: 'ZH' },
		vi: { name: 'Tiếng Việt', flag: flagVN, code: 'VI' }
	};

	const language = (locale: string) =>
		LANGUAGES[locale] ?? { name: locale.toUpperCase(), flag: flagUK, code: locale.toUpperCase() };

	const current = $derived(language(getLocale()));

	/**
	 * Every locale except the current one, in the declared order, each with the
	 * path it would land on — or `undefined` when this page has no counterpart.
	 */
	const options = $derived(
		locales
			.filter((locale) => locale !== getLocale())
			.map((locale) => ({
				locale,
				...language(locale),
				href: alternatePathFor(page, locale as SiteLocale)
			}))
	);
</script>

<div class="relative inline-block text-left">
	<!--
		`aria-label` is required, not belt-and-braces. Everything inside this
		button is either decorative (`alt=""` on the flag, `aria-hidden` on the
		chevron) or hidden below `sm` — the locale code is `hidden sm:inline` to
		keep the mobile header from overflowing — so on a phone the button had no
		accessible name at all and was announced as just "button".

		The label ends with the same locale code the button shows so that the
		accessible name still contains the visible one, which is what WCAG's
		"Label in Name" asks for.
	-->
	<button
		type="button"
		class="border-subtle inline-flex w-full cursor-pointer items-center justify-center gap-x-1.5 rounded-md border bg-surface px-2 py-2 font-semibold text-body shadow-sm transition hover:bg-surface-muted sm:px-3"
		aria-label={`${m.select_language()} — ${current.code}`}
		aria-expanded={isOpen}
		aria-haspopup="dialog"
		onclick={toggleOpen}
	>
		<img
			src={current.flag}
			alt=""
			width="24"
			height="16"
			class="border-subtle inline-block h-4 w-6 rounded-xs border object-cover sm:mr-1"
		/>
		<span class="hidden sm:inline">{current.code}</span>
		<svg class="-mr-1 h-5 w-5 text-muted" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
			<path
				fill-rule="evenodd"
				d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
				clip-rule="evenodd"
			/>
		</svg>
	</button>

	<!--
		Rendered on every page and toggled with a class, not wrapped in `{#if}`.
		Prerendering runs with the dialog closed, so an `{#if}` would keep these
		links out of the HTML entirely — and they are real `<a href>`s to each
		locale's counterpart, which is exactly the kind of internal link a crawler
		should be able to follow. `hreflang` already declares the alternates, but
		there is no reason to remove the links as well.
	-->
	<div
		role="dialog"
		aria-modal="true"
		aria-labelledby="language-dialog-title"
		class={`relative z-50 ${isOpen ? 'block' : 'hidden'}`}
	>
			<!-- Click the backdrop to dismiss, the behaviour a modal implies. -->
			<button
				type="button"
				aria-label={m.cancel()}
				onclick={handleCancel}
				class="fixed inset-0 cursor-default bg-gray-900/60 transition-opacity"
			></button>

			<div class="fixed inset-0 z-10 w-screen overflow-y-auto">
				<div class="flex min-h-full items-center justify-center p-4">
					<div
						class="relative w-full max-w-xs transform overflow-hidden rounded-lg bg-surface text-left shadow-xl transition-all"
					>
						<div class="border-subtle border-b px-5 py-4">
							<h3 id="language-dialog-title" class="text-base font-semibold text-body">
								{m.select_language()}
							</h3>
						</div>

						<!--
							One row per locale, each the same height whether or not it is a
							link, so the list does not jump as you move between pages.
						-->
						<ul class="divide-subtle max-h-[60vh] divide-y overflow-y-auto py-1">
							{#each options as option (option.locale)}
								<li>
									{#if option.href}
											<!--
											`data-sveltekit-reload` forces a full document load rather than a
											client-side navigation, and it is load-bearing.

											Changing locale changes the whole document, but nothing in a
											client-side navigation tells the app that. Paraglide caches the
											locale in module scope and only re-reads it on a fresh page load,
											and `+layout.server.ts` derives the locale from `locals`, which
											SvelteKit does not track as a URL dependency — so it reuses the
											cached payload. The result was that clicking a language changed the
											URL and the page body, while the switcher button, the whole header
											menu and the footer stayed in the previous language until you
											reloaded by hand.

											A full load is also the honest model for this site: each locale is
											its own prerendered document tree, not a re-render of one app.
										-->
										<a
											href={option.href}
											data-sveltekit-reload
											onclick={handleCancel}
											class="flex items-center gap-3 px-5 py-3 text-sm text-body transition hover:bg-surface-muted"
										>
											<img
												src={option.flag}
												alt=""
												width="28"
												height="19"
												class="border-subtle h-5 w-7 shrink-0 rounded-xs border object-cover"
											/>
											<span class="font-medium">{option.name}</span>
											<span class="text-muted ml-auto text-xs tracking-wide">{option.code}</span>
										</a>
									{:else}
										<p
											class="flex cursor-not-allowed items-center gap-3 px-5 py-3 text-sm opacity-50"
											title={m.not_available_in_language()}
										>
											<img
												src={option.flag}
												alt=""
												width="28"
												height="19"
												class="border-subtle h-5 w-7 shrink-0 rounded-xs border object-cover grayscale"
											/>
											<span class="text-muted font-medium">{option.name}</span>
											<span class="text-muted ml-auto text-right text-xs">
												{m.not_available_in_language()}
											</span>
										</p>
									{/if}
								</li>
							{/each}
						</ul>

						<div class="border-subtle border-t px-5 py-3">
							<button
								type="button"
								onclick={handleCancel}
								class="w-full cursor-pointer rounded-md px-3 py-2 text-sm font-semibold text-muted transition hover:bg-surface-muted"
							>
								{m.cancel()}
							</button>
						</div>
					</div>
			</div>
		</div>
	</div>
</div>
